import React from "react";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { UploadFileDialog } from "../../../../components/fileUpload/UploadFileDialog";
import {
  FoundationColorTokens,
  Spacing,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { UnmapMultipleSPoCsUploadPane } from "./UnmapMultipleSPoCsUploadPane";
import { NetworkingError } from "@pulse/shared-components";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import { SPoCDownloadAndUnmapStore } from "../../store/SPoCDownloadAndUnmapStore";

export interface UnmapMultipleSPoCsFileUploadComponentProps {
  t: TFunction;
  spacing: Spacing;
  tokens: FoundationColorTokens<string>;
  typography: Typography;
  navigateTo: NavigateToFunctions;
  spocDownloadAndUnmapStore: Instance<typeof SPoCDownloadAndUnmapStore> | null;
}

export const UnmapMultipleSPoCsFileUploadComponent = observer(
  ({
    t,
    spacing,
    tokens,
    typography,
    navigateTo,
    spocDownloadAndUnmapStore,
  }: UnmapMultipleSPoCsFileUploadComponentProps): React.ReactElement => {
    if (spocDownloadAndUnmapStore?.rpcErrors) {
      switch (spocDownloadAndUnmapStore.rpcErrors) {
        case NetworkingError.InternalError:
        case NetworkingError.InternalServerError: {
          navigateTo.internalServerError();
        }
      }
    }

    const getErrorBannerText = (): string | undefined => {
      if (spocDownloadAndUnmapStore?.isFileInvalid) {
        return t("common.uploadDialog.invalidFileDetailsBannerText");
      } else {
        return undefined;
      }
    };

    const dialogButtonOnClick = async (): Promise<void> => {
      spocDownloadAndUnmapStore?.setIsUnmapMultipleSPoCsUploadDialogVisible(
        false,
      );
      spocDownloadAndUnmapStore?.setIsUnmapMultipleSPoCsValidationDialogVisible(
        true,
      );
      await processProjectParams(async (projectId) => {
        await spocDownloadAndUnmapStore?.validateCSVAndUnmapSPoCs(projectId);
      }, navigateTo);
      return Promise.resolve();
    };

    return spocDownloadAndUnmapStore ? (
      <UploadFileDialog
        errorBannerText={getErrorBannerText()}
        isUploadFileDialogVisible={
          spocDownloadAndUnmapStore?.isUnmapMultipleSPoCsUploadDialogVisible
        }
        title={t("projects.userAccess.unmapSPoCs.uploadTitle", {
          defaultValue: t("common.uploadSPoCCSV"),
        })}
        description={t(
          "projects.userAccess.unmapSPoCs.downloadTemplateDescription",
          {
            defaultValue: t(
              "projects.userAccess.addMultipleSPoCsDialog.description",
            ),
          },
        )}
        primaryDialogButtonText={t("common.proceed")}
        primaryDialogButtonOnClick={dialogButtonOnClick}
        secondaryDialogButtonText={t("common.cancel")}
        isPrimaryDialogButtonDisabled={
          !spocDownloadAndUnmapStore?.hasFileUploaded ||
          spocDownloadAndUnmapStore.doesStoreContainErrors
        }
        fileUploadPane={
          <UnmapMultipleSPoCsUploadPane
            spocDownloadAndUnmapStore={spocDownloadAndUnmapStore}
            navigateTo={navigateTo}
            t={t}
            spacing={spacing}
            tokens={tokens}
            typography={typography}
          />
        }
        isSecondaryDialogButtonDisabled={spocDownloadAndUnmapStore.isRPCLoading}
        secondaryDialogButtonOnClick={() => {
          spocDownloadAndUnmapStore.resetRPCError();
          spocDownloadAndUnmapStore.resetValidationError();
          spocDownloadAndUnmapStore.resetUnmapCSVStates();
          spocDownloadAndUnmapStore?.setIsUnmapMultipleSPoCsUploadDialogVisible(
            false,
          );
        }}
      />
    ) : (
      <></>
    );
  },
);
